import styled from 'styled-components';
import { IconSizeType, SvgFileNameType } from '../../../interfaces/Element.interfaces';
import { Icon } from '../icon/Icon';

const themeColors = {
  strong: {
    primary: { background: '#196EFF', text: '#FFFFFF' },
    secondary: { background: '#FF3049', text: '#FFFFFF' },
    tertiary: { background: '#FF9500', text: '#FFFFFF' },
    quaternary: { background: '#34C77E', text: '#FFFFFF' },
    grey: { background: '#505363', text: '#FFFFFF' },
    white: { background: '#FFFFFF', text: '#232429' },
  },
  light: {
    primary: { background: '#E1FAFF', text: '#196EFF' },
    secondary: { background: '#FFDBE0', text: '#FF3049' },
    tertiary: { background: '#FFEACC', text: '#FF9500' },
    quaternary: { background: '#D6F4E5', text: '#34C77E' },
    grey: { background: '#E4E4ED', text: '#505363' },
    white: { background: '#FFFFFF', text: '#232429' },
  },
} as const;

const sizeStyles = {
  sm: {
    height: '18px',
    padding: '4px 6px',
    fontSize: '10px',
  },
  md: {
    height: '22px',
    padding: '6px 8px',
    fontSize: '12px',
  },
  lg: {
    height: '28px',
    padding: '8px',
    fontSize: '14px',
  },
} as const;

type ChipType = 'square' | 'round';
export type ChipColorType = keyof typeof themeColors.strong;
export type ChipThemeType = keyof typeof themeColors;
export type SizeType = keyof typeof sizeStyles;

const getThemeColor = (theme: ChipThemeType, color: ChipColorType) => {
  return themeColors[theme][color];
};

interface IChipIcon {
  position: 'left' | 'right';
  size: IconSizeType;
  name: SvgFileNameType;
  fill?: string;
}

interface ChipProps {
  type: ChipType;
  icon?: IChipIcon;
  color: ChipColorType;
  theme: ChipThemeType;
  size?: SizeType;
  children: string;
}

export const Chip = ({ type, icon, color, theme, children, size = 'md' }: ChipProps) => {
  const themeStyle = getThemeColor(theme, color);

  const ChipIcon: JSX.Element = icon ? <Icon name={icon.name} size={icon.size} fill={icon.fill} /> : <></>;

  return (
    <ColoredChip type={type} backgroundColor={themeStyle.background} size={size}>
      {icon && icon.position === 'left' && ChipIcon}
      <ColoredChipText color={themeStyle.text} size={size}>
        {children}
      </ColoredChipText>
      {icon && icon.position === 'right' && ChipIcon}
    </ColoredChip>
  );
};

const ColoredChip = styled.div<{ type: ChipType; backgroundColor: string; size: SizeType }>`
  height: ${(props) => sizeStyles[props.size].height};

  display: flex;
  align-items: center;

  padding: ${(props) => sizeStyles[props.size].padding};
  gap: 2px;

  border-radius: ${(props) => (props.type === 'square' ? '4px' : props.type === 'round' ? '80px' : '4px')};
  background-color: ${(props) => props.backgroundColor};
`;

const ColoredChipText = styled.div<{ color: string; size: SizeType }>`
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: ${(props) => sizeStyles[props.size].fontSize};
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
  color: ${(props) => props.color};
`;
